import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CompanySlotPacks } from "@/components/CompanySlotPacks";
import { EmployerInfoButton } from "@/components/EmployerInfo";
import { toast } from "sonner";
import {
  Search, ArrowUpDown, ChevronLeft, ChevronRight, Building2, Check, Loader2, RefreshCw, X, Zap,
} from "lucide-react";

const PAGE_SIZE = 10;
const EMPTY_DRAFT = { search: "", industry: "", location: "", size: "" };

function formatSyncTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true });
}

export function CompanyLogo({ company }) {
  const [failed, setFailed] = useState(false);
  const src = company.logo_url || (company.domain ? `https://www.google.com/s2/favicons?domain=${company.domain}&sz=64` : null);
  if (!src || failed) {
    return (
      <div className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 text-sm font-bold shrink-0">
        {(company.client_name || "?")[0].toUpperCase()}
      </div>
    );
  }
  return (
    <img src={src} alt={company.client_name} onError={() => setFailed(true)}
      className="w-8 h-8 rounded-lg object-contain bg-white border border-slate-200 p-0.5 shrink-0" />
  );
}

function SortHeader({ label, field, sort, onSort, testid }) {
  const active = sort.field === field;
  return (
    <button data-testid={testid} onClick={() => onSort(field)}
      className={`inline-flex items-center gap-1.5 font-semibold text-sm transition-colors ${active ? "text-slate-900" : "text-slate-700 hover:text-slate-900"}`}>
      {label}
      <ArrowUpDown className={`w-3.5 h-3.5 ${active ? "text-amber-500" : "text-slate-400"}`} />
    </button>
  );
}

export default function ManageCompanies() {
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [applied, setApplied] = useState(EMPTY_DRAFT);
  const [sort, setSort] = useState({ field: "created", order: "desc" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [meta, setMeta] = useState({ industries: [], locations: [], sizes: [] });
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [bridge, setBridge] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [slotQuota, setSlotQuota] = useState(null);
  const [showSlots, setShowSlots] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const fetchBridgeStatus = useCallback(async () => {
    try {
      const { data: res } = await api.get("/bridge/status");
      setBridge(res);
    } catch { /* bridge optional */ }
  }, []);

  useEffect(() => {
    api.get("/companies/meta").then((r) => setMeta(r.data)).catch(() => {});
    // Directory sync is admin-only plumbing — candidates never see it.
    api.get("/admin/access").then((r) => {
      if (r.data.is_admin) { setIsAdmin(true); fetchBridgeStatus(); }
    }).catch(() => {});
  }, [fetchBridgeStatus]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data: res } = await api.post("/bridge/sync");
      const failed = res.results.filter((r) => r.status === "error");
      const ok = res.results.filter((r) => r.status === "ok");
      if (failed.length) {
        toast.error(`Sync issue: ${failed[0].error}`);
      } else {
        toast.success(`Directory synced — ${ok.map((r) => `${r.items} ${r.module}`).join(", ")}.`);
      }
      await fetchBridgeStatus();
      setPage(1);
      fetchCompanies();
      api.get("/companies/meta").then((r) => setMeta(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Directory sync failed.");
    } finally {
      setSyncing(false);
    }
  };

  const lastSync = bridge?.states?.clients?.last_successful_sync;

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/companies", {
        params: { ...applied, sort: sort.field, order: sort.order, page, page_size: PAGE_SIZE },
      });
      setData(res);
      setSlotQuota(res.quota || null);
    } catch {
      toast.error("Could not load companies.");
    } finally {
      setLoading(false);
    }
  }, [applied, sort, page]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const applyFilters = () => { setApplied({ ...draft }); setPage(1); };
  const clearFilters = () => { setDraft(EMPTY_DRAFT); setApplied(EMPTY_DRAFT); setPage(1); };
  const onSort = (field) => {
    setSort((s) => ({ field, order: s.field === field && s.order === "desc" ? "asc" : "desc" }));
    setPage(1);
  };

  const toggleSave = async (company) => {
    if (company.saved) {
      const ok = window.confirm(
        `Remove ${company.client_name} from saved companies?\n\nHeads up: the company credit used to save it is NOT refunded. Saving it again later counts as a new company credit.`
      );
      if (!ok) return;
    }
    setSavingId(company.id);
    try {
      if (company.saved) {
        await api.delete(`/companies/${company.id}/save`);
      } else {
        await api.post(`/companies/${company.id}/save`);
      }
      setData((d) => ({ ...d, items: d.items.map((c) => (c.id === company.id ? { ...c, saved: !c.saved } : c)) }));
      toast.success(company.saved ? `${company.client_name} removed from saved.` : `${company.client_name} saved — 1 company credit used.`);
      fetchCompanies();
    } catch (e) {
      if (e.response?.status === 402) {
        setShowSlots(true);
        toast.error(typeof e.response.data?.detail === "string" ? e.response.data.detail : "You've used all your company credits.");
      } else {
        toast.error("Could not update saved state.");
      }
    } finally {
      setSavingId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const showingFrom = data.total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const showingTo = Math.min(page * PAGE_SIZE, data.total);
  const pageNumbers = useMemo(() => {
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    return Array.from({ length: Math.min(5, totalPages) }, (_, i) => start + i);
  }, [page, totalPages]);

  const selectCls = "h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-700 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/30";

  return (
    <div data-testid="manage-companies-page" className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-display">Companies</h1>
          <p className="text-slate-500 mt-1">Browse and save companies from our network to stay updated on opportunities.</p>
          <p className="text-xs text-slate-400 mt-1">
            Saving a company uses 1 company credit (lifetime — removing a saved company does not refund its credit).
          </p>
        </div>
        <div className="flex items-center gap-3">
          {slotQuota && (
            <span data-testid="company-credits-indicator"
              className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-1.5 text-xs font-semibold text-amber-800">
              <Zap className="w-3.5 h-3.5" />
              {slotQuota.remaining == null ? "Company credits: Unlimited" : `Company credits: ${slotQuota.remaining} left`}
            </span>
          )}
          {isAdmin && (
            <>
              {lastSync && (
                <span className="text-xs text-slate-400" data-testid="bridge-last-sync">Synced {formatSyncTime(lastSync)}</span>
              )}
              <Button data-testid="bridge-sync-button" onClick={handleSync} disabled={syncing}
                variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50 font-semibold">
                <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? "Syncing…" : "Sync Directory"}
              </Button>
            </>
          )}
        </div>
      </div>

      {showSlots && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5" data-testid="company-buy-prompt">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-bold text-slate-900">Need more companies?</h2>
              <p className="text-sm text-slate-500 mt-1">You've used all your company credits. Top up below — purchased slots never expire.</p>
            </div>
            <button data-testid="company-buy-close" onClick={() => setShowSlots(false)}
              className="text-slate-400 hover:text-slate-700 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-4 max-w-xl">
            <CompanySlotPacks onPurchased={() => { setShowSlots(false); fetchCompanies(); }} />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-col lg:flex-row gap-3 lg:items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="companies-search-input"
            value={draft.search}
            onChange={(e) => setDraft((d) => ({ ...d, search: e.target.value }))}
            onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
            placeholder="Search by company name or contact name"
            className="pl-9 h-11 bg-white border-slate-300 text-slate-900"
          />
        </div>
        <select data-testid="companies-industry-filter" value={draft.industry}
          onChange={(e) => setDraft((d) => ({ ...d, industry: e.target.value }))} className={selectCls}>
          <option value="">All Industries</option>
          {meta.industries.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
        <select data-testid="companies-location-filter" value={draft.location}
          onChange={(e) => setDraft((d) => ({ ...d, location: e.target.value }))} className={selectCls}>
          <option value="">All Locations</option>
          {meta.locations.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select data-testid="companies-size-filter" value={draft.size}
          onChange={(e) => setDraft((d) => ({ ...d, size: e.target.value }))} className={selectCls}>
          <option value="">All Company Sizes</option>
          {meta.sizes.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="flex items-center gap-3 lg:ml-auto">
          <button data-testid="companies-clear-filters" onClick={clearFilters}
            className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors whitespace-nowrap">
            Clear Filters
          </button>
          <Button data-testid="companies-search-button" onClick={applyFilters}
            className="h-11 px-8 bg-amber-500 text-black hover:bg-amber-400 font-semibold transition-colors">
            Search
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px]" data-testid="companies-table">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                <th className="pl-5 pr-2 py-3.5 w-10"><input type="checkbox" className="w-4 h-4 accent-amber-500" data-testid="companies-select-all" /></th>
                <th className="px-3 py-3.5"><SortHeader label="Client Name" field="client_name" sort={sort} onSort={onSort} testid="sort-client" /></th>
                <th className="px-3 py-3.5"><SortHeader label="Contact Name" field="contact_name" sort={sort} onSort={onSort} testid="sort-contact" /></th>
                <th className="px-3 py-3.5 pr-6 text-right text-sm font-semibold text-slate-700">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="py-16 text-center">
                  <Loader2 className="w-7 h-7 mx-auto animate-spin text-amber-500" />
                </td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={4} className="py-16 text-center text-slate-500">
                  <Building2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium text-slate-700">No companies found</p>
                  <p className="text-sm mt-1">Try adjusting your search or filters.</p>
                </td></tr>
              ) : (
                data.items.map((c) => (
                  <tr key={c.id} data-testid={`company-row-${c.id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors">
                    <td className="pl-5 pr-2 py-3.5"><input type="checkbox" className="w-4 h-4 accent-amber-500" data-testid={`company-check-${c.id}`} /></td>
                    <td className="px-3 py-3.5">
                      <div className="flex items-center gap-3">
                        <CompanyLogo company={c} />
                        <span className="text-sm font-medium text-slate-900">{c.client_name}</span>
                        <EmployerInfoButton company={c.client_name} testid={`company-info-${c.id}`} />
                        {c.new_openings && (
                          <span className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 text-[11px] font-semibold">New openings</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3.5 text-sm text-slate-600">{c.contact_name || "—"}</td>
                    <td className="px-3 py-3.5 pr-6 text-right">
                      <button
                        data-testid={`company-save-${c.id}`}
                        onClick={() => toggleSave(c)}
                        disabled={savingId === c.id}
                        className={`inline-flex items-center gap-1.5 px-5 py-1.5 rounded-lg border text-sm font-semibold transition-colors ${
                          c.saved
                            ? "bg-amber-500 border-amber-500 text-black"
                            : "border-amber-400 text-amber-600 hover:bg-amber-50"
                        }`}
                      >
                        {c.saved && <Check className="w-3.5 h-3.5" />}
                        {c.saved ? "Saved" : "Save"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-5 py-4 border-t border-slate-100" data-testid="companies-pagination">
          <p className="text-sm text-slate-500">Showing {showingFrom} - {showingTo} of {data.total} companies</p>
          <div className="flex items-center gap-1.5">
            <button data-testid="companies-prev" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
              className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40 transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            {pageNumbers.map((n) => (
              <button key={n} data-testid={`companies-page-${n}`} onClick={() => setPage(n)}
                className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                  n === page ? "bg-amber-500 text-black" : "text-slate-600 hover:bg-slate-100"
                }`}>
                {n}
              </button>
            ))}
            <button data-testid="companies-next" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40 transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
